import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faStar,
  faUsers,
  faAward,
  faArrowRight,
  faSolarPanel,
  faWind,
  faWater,
  faCheckCircle,
  faGlobe,
  faLightbulb,
  faChartLine,
  faLeaf,
  faQuoteLeft,
  faCalendarAlt,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faShieldAlt,
  faCog,
  faBolt,
  faRecycle,
  faCalculator,
  faSun,
  faDollarSign,
  faHome,
  faIndustry,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import SEO from './SEO';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  // Solar Calculator State
  const [calculatorData, setCalculatorData] = useState({
    monthlyBill: '',
    roofArea: '',
    location: 'residential',
    sunlightHours: 5,
    systemSize: 0,
    totalCost: 0,
    annualSavings: 0,
    paybackPeriod: 0,
    carbonOffset: 0,
    // New display fields (strings) to show ranges without altering layout
    totalCostDisplay: '',
    monthlySavingsRangeDisplay: '',
    annualSavingsRangeDisplay: '',
    paybackPeriodRangeDisplay: ''
  });

  const [showCalculator, setShowCalculator] = useState(false);

  const heroSlides = [
    {
      image: '/images/photovoltaics-factory-investors-evaluate-solar-panels-addressing-issues.jpg',
      title: 'Solar Energy Solutions',
      subtitle: 'Harness the Power of the Sun',
      description: 'Transform your energy consumption with cutting-edge solar panel technology. Reduce costs and carbon footprint simultaneously.',
      icon: faSolarPanel,
      color: 'from-primary-green to-primary-green-light'
    },
    {
      image: '/images/wind-turbines-solar-panels-field-yellow-flowers.jpg',
      title: 'Wind Power Innovation',
      subtitle: 'Clean Energy from Wind',
      description: 'Advanced wind turbine solutions for sustainable energy generation. Power your future with renewable wind energy.',
      icon: faWind,
      color: 'from-secondary-blue to-secondary-blue-dark'
    },
    {
      image: '/images/house-with-solar-panels-garden.jpg',
      title: 'Residential Solar Solutions',
      subtitle: 'Power Your Home with Solar',
      description: 'Beautiful and efficient solar panel installations for homes. Increase property value while saving on energy bills.',
      icon: faSolarPanel,
      color: 'from-primary-green to-secondary-blue'
    },
    {
      image: '/images/solar-panels-red-tile-roof-with-clear-blue-sky-background.jpg',
      title: 'Commercial Solar Systems',
      subtitle: 'Business Energy Solutions',
      description: 'Large-scale solar installations for businesses and commercial properties. Maximize ROI with sustainable energy.',
      icon: faSolarPanel,
      color: 'from-secondary-blue to-primary-green'
    },
    {
      image: '/images/man-installing-solar-panels-roof.jpg',
      title: 'Professional Installation',
      subtitle: 'Expert Solar Panel Installation',
      description: 'Professional installation services with certified technicians ensuring quality and safety for your solar project.',
      icon: faSolarPanel,
      color: 'from-primary-green to-secondary-blue-dark'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
          setIsAnimating(false);
        }, 1000);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isAnimating, heroSlides.length]);

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        setIsAnimating(false);
      }, 1000);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
        setIsAnimating(false);
      }, 1000);
    }
  };


    const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  //handlew form submit function 
  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    const formData = new FormData(event.target);

    formData.append("access_key", "05aa0895-6627-433c-83d8-566602f3a414");

    const object = Object.fromEntries(formData);
    const json = JSON.stringify(object);

    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: json
    }).then((res) => res.json());

    if (res.success) {
      console.log("Success", res);
      alert("message sent succesfully");
    }
    setLoading(false);

  };


  const goToSlide = (index) => {
    if (!isAnimating && index !== currentSlide) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 1000);
    }
  };

  // Solar Calculator Functions
  const calculateSolar = () => {
    const monthlyBill = parseFloat(calculatorData.monthlyBill) || 0;
    const roofArea = parseFloat(calculatorData.roofArea) || 0;

    // Assumptions / constants
    const costPerKW = 60000; // ₹ per kW
    const subsidyPercent = 30; // %
    const unitsPerKWPerMonth = 120; // 1kW ≈ 120 units/month
    const tariffPerUnit = 8; // ₹ per unit (kWh)

    // Estimate system size primarily from roof area; fallback to bill-to-kWh sizing
    // 1 kW ≈ 100 sq ft
    const systemSizeFromRoof = roofArea > 0 ? (roofArea / 100) : 0;

    // Alternative sizing from bill: monthly kWh ≈ bill / tariff; systemSize = monthlyKWh/unitsPerKWPerMonth
    const monthlyKWh = monthlyBill > 0 ? (monthlyBill / tariffPerUnit) : 0;
    const systemSizeFromBill = monthlyKWh > 0 ? (monthlyKWh / unitsPerKWPerMonth) : 0;

    // Choose the reasonable system size (min of both if both provided, else the one available)
    let systemSizeKW = 0;
    if (systemSizeFromRoof && systemSizeFromBill) {
      systemSizeKW = Math.min(systemSizeFromRoof, systemSizeFromBill);
    } else {
      systemSizeKW = systemSizeFromRoof || systemSizeFromBill || 0;
    }

    // Core calculations
    const costBeforeSubsidy = systemSizeKW * costPerKW; // Cost before subsidy
    const subsidyAmount = costBeforeSubsidy * (subsidyPercent / 100);
    const costAfterSubsidy = Math.max(0, costBeforeSubsidy - subsidyAmount);

    // Savings: based on generation or bill
    const monthlyGenerationUnits = systemSizeKW * unitsPerKWPerMonth; // units/month
    const monthlySavingsByGeneration = monthlyGenerationUnits * tariffPerUnit; // ₹

    // Provide a range for savings: 80% - 100% of generation value
    const monthlySavingsMin = monthlySavingsByGeneration * 0.8;
    const monthlySavingsMax = monthlySavingsByGeneration * 1.0;

    const annualSavingsMin = monthlySavingsMin * 12;
    const annualSavingsMax = monthlySavingsMax * 12;

    // Payback period range
    const paybackMinYears = annualSavingsMax > 0 ? (costAfterSubsidy / annualSavingsMax) : 0;
    const paybackMaxYears = annualSavingsMin > 0 ? (costAfterSubsidy / annualSavingsMin) : 0;

    // Formatting helpers
    const toLakh = (amount) => amount / 100000; // convert to lakh
    const formatINR = (n) => Math.round(n).toLocaleString('en-IN');

    // Build display strings
    const totalCostLakh = toLakh(costAfterSubsidy);
    const totalCostDisplay = `₹ ${totalCostLakh.toFixed(2)} lakh (approx.)`;
    const monthlySavingsRangeDisplay = `₹ ${formatINR(monthlySavingsMin)} – ₹ ${formatINR(monthlySavingsMax)}`;
    const annualSavingsRangeDisplay = `₹ ${formatINR(annualSavingsMin)} – ₹ ${formatINR(annualSavingsMax)}`;
    const paybackPeriodRangeDisplay = `${paybackMinYears.toFixed(1)} – ${paybackMaxYears.toFixed(1)} years`;

    // Maintain existing fields for current UI while storing new displays
    setCalculatorData(prev => ({
      ...prev,
      systemSize: Math.round(systemSizeKW * 10) / 10,
      totalCost: Math.round(costAfterSubsidy),
      annualSavings: Math.round((annualSavingsMin + annualSavingsMax) / 2), // keep a representative value for existing UI card
      paybackPeriod: Math.round(((paybackMinYears + paybackMaxYears) / 2) * 10) / 10,
      carbonOffset: Math.round((monthlyGenerationUnits * 12) * 0.92),
      totalCostDisplay,
      monthlySavingsRangeDisplay,
      annualSavingsRangeDisplay,
      paybackPeriodRangeDisplay
    }));
  };

  const handleCalculatorInput = (field, value) => {
    setCalculatorData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetCalculator = () => {
    setCalculatorData({
      monthlyBill: '',
      roofArea: '',
      location: 'residential',
      sunlightHours: 5,
      systemSize: 0,
      totalCost: 0,
      annualSavings: 0,
      paybackPeriod: 0,
      carbonOffset: 0,
      totalCostDisplay: '',
      monthlySavingsRangeDisplay: '',
      annualSavingsRangeDisplay: '',
      paybackPeriodRangeDisplay: ''
    });
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Corenova | Renewable Energy & Solar Installations Odisha"
        description="Corenova brings fast, hassle‑free solar installations across Odisha. expert consultation to 24/7 support, powering homes and businesses toward zero electricity bills."
        canonical="https://corenovarenewable.com/"
        image="https://corenovarenewable.com/images/2.png"
        keywords="solar panels, solar installation, renewable energy India, green energy, CoreNova"
      />
      {/* Hero Section with Sliding Images */}
      <section className="relative h-screen overflow-hidden">
        {/* Background Images */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-2000 ease-in-out ${index === currentSlide
                ? 'opacity-100 translate-x-0 scale-100'
                : index < currentSlide
                  ? 'opacity-0 -translate-x-full scale-95'
                  : 'opacity-0 translate-x-full scale-95'
              }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-2000 ease-in-out"
              style={{
                backgroundImage: `url(${slide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/50" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="container mx-auto px-4 text-center text-white">
            <div className="transition-opacity duration-500 ease-in-out">
              <div className="mb-6">
                <FontAwesomeIcon
                  icon={heroSlides[currentSlide].icon}
                  className="text-6xl md:text-8xl mb-4 animate-pulse-slow"
                />
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4">
                {heroSlides[currentSlide].title}
              </h1>

              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-6 text-blue-200">
                {heroSlides[currentSlide].subtitle}
              </h2>

              <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
                {heroSlides[currentSlide].description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href
                  ="/services"
                  className={`btn btn-primary bg-gradient-to-r ${heroSlides[currentSlide].color} hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
                >
                  Explore Services
                  <FontAwesomeIcon icon={faArrowRight} />
                </a>
                <a href="/contact" className="btn btn-outline border-white text-white hover:bg-white hover:text-dark">
                  <FontAwesomeIcon icon={faComments} />
                  Get a Free Consultation
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-4 rounded-full transition-all duration-300 z-20 backdrop-blur-sm border border-white/20"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-4 rounded-full transition-all duration-300 z-20 backdrop-blur-sm border border-white/20"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${index === currentSlide
                  ? 'bg-white scale-125 shadow-lg'
                  : 'bg-white/50 hover:bg-white/75 hover:scale-110'
                }`}
            />
          ))}
        </div>
      </section>

      {/* What is Solar Technology */}
{/* What is Solar Technology */}
<section className="section bg-gray-50 py-20">
  <div className="container">
    {/* Section Header */}
    <div className="text-center mb-16">
      <div className="w-20 h-20 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
        <FontAwesomeIcon icon={faSun} className="text-3xl text-white" />
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
        What is Solar Technology?
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
        Solar power is the fastest expanding source of renewable energy in the world and will
        be the major contributor to our future energy mix of carbon neutrality.
      </p>
    </div>

    {/* Main Content */}
    <div className="space-y-16">
      {/* Feature Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group">
          <div className="w-16 h-16 bg-primary-green/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <FontAwesomeIcon icon={faSun} className="text-2xl text-primary-green" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary-green transition-colors duration-300">
            Harnessing Sun's Energy
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Utilizing and converting the energy from the sun into electricity is not only the lowest
            cost source of renewable energy services.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group">
          <div className="w-16 h-16 bg-secondary-blue/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <FontAwesomeIcon icon={faChartLine} className="text-2xl text-secondary-blue" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary-green transition-colors duration-300">
            Scalable Solutions
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Solar technology can be deployed at scale and speed either through rooftop solar systems
            or large utility solar farms.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group">
          <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <FontAwesomeIcon icon={faBolt} className="text-2xl text-yellow-600" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary-green transition-colors duration-300">
            Direct Conversion
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Advanced photovoltaic technology converts sunlight directly into electricity with maximum 
            efficiency and no moving parts.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <FontAwesomeIcon icon={faRecycle} className="text-2xl text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary-green transition-colors duration-300">
            Sustainable Future
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Zero emissions technology with infinite renewable source potential, contributing to 
            global carbon neutrality goals.
          </p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Left Column */}
        <div className="space-y-8">
          {/* Complementary Energy Card */}
          <div className="bg-gradient-to-r from-primary-green/5 to-secondary-blue/5 rounded-2xl p-8 border border-primary-green/20 hover:shadow-lg transition-all duration-300 group">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <FontAwesomeIcon icon={faWind} className="text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary-green transition-colors duration-300">
                  Complementary Energy Systems
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Solar energy complements onshore and offshore wind power to create a comprehensive 
                  renewable energy mix that meets the diverse needs of consumers worldwide.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
            <h4 className="text-lg font-bold text-dark mb-4">Key Benefits</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 hover:bg-white/80 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faLeaf} className="text-white text-lg" />
                </div>
                <div>
                  <h5 className="font-bold text-dark mb-1">Environmental Impact</h5>
                  <p className="text-gray-600 text-sm">Reduces carbon footprint and contributes to cleaner air quality</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 hover:bg-white/80 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-white text-lg" />
                </div>
                <div>
                  <h5 className="font-bold text-dark mb-1">Energy Independence</h5>
                  <p className="text-gray-600 text-sm">Reduces reliance on fossil fuels and volatile energy markets</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 hover:bg-white/80 transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon icon={faIndustry} className="text-white text-lg" />
                </div>
                <div>
                  <h5 className="font-bold text-dark mb-1">Economic Growth</h5>
                  <p className="text-gray-600 text-sm">Creates jobs and stimulates local economic development</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Call to Action */}
          <div className="bg-gradient-to-r from-primary-green/10 to-secondary-blue/10 rounded-2xl p-8 text-center border border-primary-green/20">
            <h3 className="text-2xl font-bold text-dark mb-4">
              Ready to Embrace Solar Technology?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Discover how solar technology can transform your energy consumption and contribute to a sustainable future.
            </p>
            <a href
            ="/services/solar"
              className="btn btn-primary bg-gradient-to-r from-primary-green to-secondary-blue px-8 py-4 text-lg inline-flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300 rounded-xl text-white font-semibold"
            >
              Learn More About Solar Technology
              <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
            </a>
          </div>
          
          {/* Quick Facts */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-lg font-bold text-dark mb-4 text-center">Solar Technology Facts</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Energy Source</span>
                <span className="font-semibold text-dark">100% Renewable</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Maintenance</span>
                <span className="font-semibold text-dark">Minimal Required</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Installation Time</span>
                <span className="font-semibold text-dark">1-3 Days</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Warranty</span>
                <span className="font-semibold text-dark">20-25 Years</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
          <div className="text-4xl font-bold text-transparent bg-gradient-to-r from-primary-green to-secondary-blue bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">
            25+
          </div>
          <div className="text-lg font-semibold text-dark mb-2">Years Lifespan</div>
          <div className="text-sm text-gray-600">Long-term reliable energy generation</div>
        </div>

        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
          <div className="text-4xl font-bold text-transparent bg-gradient-to-r from-primary-green to-secondary-blue bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">
            80%
          </div>
          <div className="text-lg font-semibold text-dark mb-2">Cost Reduction</div>
          <div className="text-sm text-gray-600">Dramatic decrease in solar costs over the past decade</div>
        </div>

        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
          <div className="text-4xl font-bold text-transparent bg-gradient-to-r from-primary-green to-secondary-blue bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">
            100M+
          </div>
          <div className="text-lg font-semibold text-dark mb-2">Homes Powered</div>
          <div className="text-sm text-gray-600">Global solar capacity continues to expand rapidly</div>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Features Section */}
      <section className="section bg-gray-light">
        <div className="container">
          <div className="section-title">
            <h2>Why Choose Renewable Energy?</h2>
            <p>It is vital for environmental, economic, and strategic reasons to choose renewable
              energy. Renewable systems can offer a continued supply to stakeholders, protect
              stakeholders from fluctuations in fossil fuel prices, and produce no greenhouse gas
              emissions, which relates to climate action directly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group animate-on-scroll flex flex-col">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faSolarPanel} className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">Solar Power Solutions</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Harness the power of the sun with our advanced solar panel installations and energy storage systems.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Residential & Commercial</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Grid Integration</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Maintenance Support</span>
                </li>
              </ul>
              <div className="mt-auto">
                <Link
                  to="/services/solar"
                  className="btn btn-outline border-primary-green text-primary-green hover:bg-primary-green hover:text-white w-full inline-flex items-center justify-center gap-2"
                >
                  Learn More
                  <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                </Link>
              </div>
            </div>

            <div className="card group animate-on-scroll flex flex-col">
              <div className="w-20 h-20 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faWind} className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">Wind Energy Systems</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Generate clean electricity with our state-of-the-art wind turbine technology and infrastructure.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Onshore & Offshore</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Smart Monitoring</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Performance Optimization</span>
                </li>
              </ul>
              <div className="mt-auto">
                <Link
                  to="/services/wind"
                  className="btn btn-outline border-secondary-blue text-secondary-blue hover:bg-secondary-blue hover:text-white w-full inline-flex items-center justify-center gap-2"
                >
                  Learn More
                  <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                </Link>
              </div>
            </div>

            <div className="card group animate-on-scroll flex flex-col">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faWater} className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">Hydroelectric Power</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Utilize water resources efficiently with our hydroelectric power generation and management systems.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Small & Large Scale</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Environmental Impact</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Sustainable Design</span>
                </li>
              </ul>
              <div className="mt-auto">
                <Link
                  to="/services/hydro"
                  className="btn btn-outline border-primary-green text-primary-green hover:bg-primary-green hover:text-white w-full inline-flex items-center justify-center gap-2"
                >
                  Learn More
                  <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solar Calculator Section */}
      <section className="section bg-gradient-to-br from-primary-green-lighter to-secondary-blue-light">
        <div className="container">
          <div className="section-title text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-4">
              <FontAwesomeIcon icon={faCalculator} className="mr-4 text-primary-green" />
              Solar Cost Calculator
            </h2>
            <p className="text-xl text-gray max-w-3xl mx-auto">
              Calculate your potential solar savings and system requirements in just a few clicks.
              Get instant estimates for your solar panel installation with Indian pricing and subsidies.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calculator Form */}
              <div className="card bg-white/80 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-dark mb-6 flex items-center">
                  <FontAwesomeIcon icon={faSun} className="mr-3 text-primary-green" />
                  Calculate Your Solar Needs
                </h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-dark font-semibold mb-2">
                      Monthly Electricity Bill (₹)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 5000 (₹5,000 per month)"
                      value={calculatorData.monthlyBill}
                      onChange={(e) => handleCalculatorInput('monthlyBill', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                    />
                    <p className="text-sm text-gray mt-1">Average Indian household spends ₹3,000-8,000 monthly</p>
                  </div>

                  <div>
                    <label className="block text-dark font-semibold mb-2">
                      Roof Area (sq ft)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 1000 sq ft"
                      value={calculatorData.roofArea}
                      onChange={(e) => handleCalculatorInput('roofArea', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                    />
                    <p className="text-sm text-gray mt-1">1 kW system needs ~100 sq ft of roof space</p>
                  </div>

                  <div>
                    <label className="block text-dark font-semibold mb-2">
                      Property Type
                    </label>
                    <select
                      value={calculatorData.location}
                      onChange={(e) => handleCalculatorInput('location', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="industrial">Industrial</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-dark font-semibold mb-2">
                      Daily Sunlight Hours
                    </label>
                    <input
                      type="range"
                      min="3"
                      max="8"
                      step="0.5"
                      value={calculatorData.sunlightHours}
                      onChange={(e) => handleCalculatorInput('sunlightHours', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray mt-1">
                      <span>3h</span>
                      <span className="font-semibold">{calculatorData.sunlightHours}h</span>
                      <span>8h</span>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={calculateSolar}
                      className="flex-1 btn btn-primary bg-gradient-to-r from-primary-green to-secondary-blue hover:shadow-xl"
                    >
                      <FontAwesomeIcon icon={faCalculator} className="mr-2" />
                      Calculate
                    </button>
                    <button
                      onClick={resetCalculator}
                      className="px-6 py-3 bg-gray-200 text-dark rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Display */}
              <div className="card bg-white/80 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-dark mb-6 flex items-center">
                  <FontAwesomeIcon icon={faDollarSign} className="mr-3 text-secondary-blue" />
                  Your Solar Analysis
                </h3>

                {calculatorData.totalCost > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-r from-primary-green to-primary-green-light text-white p-4 rounded-xl">
                        <div className="text-sm opacity-90">System Size</div>
                        <div className="text-2xl font-bold">{calculatorData.systemSize} kW</div>
                      </div>
                      <div className="bg-gradient-to-r from-secondary-blue to-secondary-blue-dark text-white p-4 rounded-xl">
                        <div className="text-sm opacity-90">Cost after Subsidy</div>
                        <div className="text-2xl font-bold">{calculatorData.totalCostDisplay}</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faDollarSign} className="text-green-600 mr-3" />
                          <span className="font-semibold text-dark">Monthly Savings</span>
                        </div>
                        <span className="text-2xl font-bold text-green-600">{calculatorData.monthlySavingsRangeDisplay}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faDollarSign} className="text-blue-600 mr-3" />
                          <span className="font-semibold text-dark">Annual Savings</span>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">{calculatorData.annualSavingsRangeDisplay}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-green-600 mr-3" />
                          <span className="font-semibold text-dark">Payback Period</span>
                        </div>
                        <span className="text-2xl font-bold text-green-600">{calculatorData.paybackPeriodRangeDisplay}</span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary-green to-secondary-blue text-white p-6 rounded-xl">
                      <h4 className="text-xl font-bold mb-2">Ready to Go Solar?</h4>
                      <p className="mb-4 opacity-90">
                        Based on your calculations, you could save ₹{calculatorData.annualSavings.toLocaleString()} annually!
                      </p>
                      <div className="mb-4 p-3 bg-white/20 rounded-lg">
                        <p className="text-sm opacity-90">
                          <strong>Indian Benefits:</strong> 30% subsidy available, 5-year warranty, net metering benefits
                        </p>
                      </div>
                      <Link to="/contact" className="inline-flex items-center bg-white text-primary-green px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                        Get Free Quote
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faCalculator} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray text-lg">
                      Enter your details and click "Calculate" to see your solar analysis
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-on-scroll">
              <h2 className="text-4xl md:text-5xl font-bold text-dark leading-tight">
                Benefits of Renewable Energy
              </h2>
              <p className="text-xl text-gray leading-relaxed">
                Switching to renewable energy offers numerous advantages for businesses, communities, and the environment.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-gray-light rounded-2xl hover:bg-gradient-to-r hover:from-primary-green/10 hover:to-secondary-blue/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-green to-primary-green-light rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faGlobe} className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark mb-2">Environmental Protection</h4>
                    <p className="text-gray">Helps cut down greenhouse gas emissions, lowers pollution, and plays a vital role in fighting
                      climate change.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-light rounded-2xl hover:bg-gradient-to-r hover:from-primary-green/10 hover:to-secondary-blue/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faLightbulb} className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark mb-2">Cost Savings</h4>
                    <p className="text-gray">Reduces electricity expenses and provides steady long-term financial advantages through lower
                      operating costs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-light rounded-2xl hover:bg-gradient-to-r hover:from-primary-green/10 hover:to-secondary-blue/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-green to-secondary-blue rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faChartLine} className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark mb-2">Energy Independence</h4>
                    <p className="text-gray">Decreases dependence on fossil fuels and imported energy, giving greater security and control
                      over energy needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="animate-on-scroll">
              <div className="relative">
                <div className="w-80 h-80 bg-gradient-to-br from-primary-green-lighter to-secondary-blue-light rounded-full mx-auto flex flex-col items-center justify-center text-white shadow-2xl">
                  <FontAwesomeIcon icon={faLeaf} className="text-6xl mb-4" />
                  <p className="text-xl font-semibold">Clean Energy Future</p>
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center animate-pulse-slow">
                  <FontAwesomeIcon icon={faStar} className="text-white text-2xl" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-primary-green rounded-full flex items-center justify-center animate-pulse-slow" style={{ animationDelay: '1s' }}>
                  <FontAwesomeIcon icon={faAward} className="text-white text-xl" />
                </div>
              </div>
            </div>

            {/* Indian Solar Information */}
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white/60 rounded-xl">
                <FontAwesomeIcon icon={faAward} className="text-3xl text-primary-green mb-3" />
                <h4 className="font-bold text-dark mb-2">Government Subsidy</h4>
                <p className="text-sm text-gray">Up to 30% subsidy available under PM-KUSUM scheme</p>
              </div>
              <div className="text-center p-6 bg-white/60 rounded-xl">
                <FontAwesomeIcon icon={faShieldAlt} className="text-3xl text-secondary-blue mb-3" />
                <h4 className="font-bold text-dark mb-2">5-Year Warranty</h4>
                <p className="text-sm text-gray">Comprehensive warranty coverage on all installations</p>
              </div>
              <div className="text-center p-6 bg-white/60 rounded-xl">
                <FontAwesomeIcon icon={faBolt} className="text-3xl text-primary-green mb-3" />
                <h4 className="font-bold text-dark mb-2">Net Metering</h4>
                <p className="text-sm text-gray">Sell excess power back to the grid and earn credits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-gradient-to-r from-primary-green to-secondary-blue text-white">
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="animate-on-scroll">
              <div className="text-5xl font-bold mb-2">1+</div>
              <div className="text-lg opacity-90">Years Experience</div>
            </div>
            <div className="animate-on-scroll">
              <div className="text-5xl font-bold mb-2">1000+</div>
              <div className="text-lg opacity-90">Happy Clients</div>
            </div>
            <div className="animate-on-scroll">
              <div className="text-5xl font-bold mb-2">50MW</div>
              <div className="text-lg opacity-90">Energy Generated</div>
            </div>
            <div className="animate-on-scroll">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-lg opacity-90">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section className="section bg-gray-light">
        <div className="container">
          <div className="section-title">
            <h2>Advanced Technology</h2>
            <p>Cutting-edge renewable energy solutions powered by the latest technology</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="card group hover:shadow-2xl transition-all duration-500">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faBolt} className="text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">Smart Grid Integration</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Advanced smart grid technology that optimizes energy distribution and reduces waste through intelligent monitoring systems.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Real-time Monitoring</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Predictive Analytics</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Automated Optimization</span>
                </li>
              </ul>
            </div>

            <div className="card group hover:shadow-2xl transition-all duration-500">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faCog} className="text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">IoT Energy Management</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Internet of Things technology for comprehensive energy management and remote monitoring capabilities.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Remote Monitoring</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Mobile App Control</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Data Analytics</span>
                </li>
              </ul>
            </div>

            <div className="card group hover:shadow-2xl transition-all duration-500">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FontAwesomeIcon icon={faRecycle} className="text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-dark mb-4">Energy Storage Solutions</h3>
              <p className="text-gray mb-6 leading-relaxed">
                Advanced battery storage systems that maximize energy efficiency and provide backup power solutions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Lithium-ion Batteries</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Backup Power</span>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-primary-green" />
                  <span className="text-dark">Peak Shaving</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Projects */}
      <section className="section bg-white">
        <div className="container">
          <div className="section-title">
            <h2>Latest Projects</h2>
            <p>Discover our recent renewable energy installations and success stories</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl mb-6">
                <img
                  src="/images/house-with-solar-panels-garden.jpg"
                  alt="Residential Solar Project"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-sm font-semibold">Residential Project</div>
                  <div className="text-xs opacity-90">5kW Solar Installation</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-dark mb-2 group-hover:text-primary-green transition-colors">Modern Home Solar</h3>
              <p className="text-gray text-sm">Complete solar panel installation for a modern family home with energy storage system.</p>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl mb-6">
                <img
                  src="/images/solar-panels-red-tile-roof-with-clear-blue-sky-background.jpg"
                  alt="Commercial Solar Project"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-sm font-semibold">Commercial Project</div>
                  <div className="text-xs opacity-90">100kW Solar Farm</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-dark mb-2 group-hover:text-primary-green transition-colors">Business Solar Farm</h3>
              <p className="text-gray text-sm">Large-scale commercial solar installation providing clean energy for manufacturing facility.</p>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl mb-6">
                <img
                  src="/images/wind-turbines-solar-panels-field-yellow-flowers.jpg"
                  alt="Mixed Energy Project"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-sm font-semibold">Hybrid Project</div>
                  <div className="text-xs opacity-90">Solar + Wind Farm</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-dark mb-2 group-hover:text-primary-green transition-colors">Hybrid Energy Park</h3>
              <p className="text-gray text-sm">Combined solar and wind energy installation maximizing renewable energy generation.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn btn-primary">
              View All Projects
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-gradient-to-br from-gray-light to-white">
        <div className="container">
          <div className="section-title">
            <h2>What Our Clients Say</h2>
            <p>Real feedback from satisfied customers who have transformed their energy consumption</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card text-center group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-primary-green-light rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faQuoteLeft} className="text-2xl text-white" />
              </div>
              <p className="text-gray mb-6 italic leading-relaxed">
                "Corenova transformed our home with a beautiful solar installation. Our energy bills have dropped by 80% and the system looks amazing!"
              </p>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <h4 className="font-bold text-dark">Rajesh kumar </h4>
              <p className="text-sm text-gray">Homeowner</p>
            </div>

            <div className="card text-center group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary-blue to-secondary-blue-dark rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faQuoteLeft} className="text-2xl text-white" />
              </div>
              <p className="text-gray mb-6 italic leading-relaxed">
                "The commercial solar installation exceeded our expectations. Professional service, quality equipment, and excellent ROI."
              </p>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <h4 className="font-bold text-dark">DR Bijoy Mohanty </h4>
              <p className="text-sm text-gray">Business Owner</p>
            </div>

            <div className="card text-center group hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faQuoteLeft} className="text-2xl text-white" />
              </div>
              <p className="text-gray mb-6 italic leading-relaxed">
                "Outstanding service from start to finish. The team was knowledgeable, professional, and delivered exactly what was promised."
              </p>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <h4 className="font-bold text-dark"> Nanigopal Das
              </h4>
              <p className="text-sm text-gray">Property Manager</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section bg-gradient-to-r from-dark to-gray-800 text-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                Ready to Start Your Renewable Energy Journey?
              </h2>
              <p className="text-xl opacity-90 leading-relaxed">
                Get in touch with our experts today and discover how renewable energy can transform your life and business.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faPhone} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Call Us</div>
                    <a
                      href="tel:+918093237123"
                      className="opacity-80 hover:opacity-100 hover:text-primary-green transition-all duration-300"
                    >
                      +91 8093237123
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faEnvelope} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Email Us</div>
                    <a
                      href="mailto:corenovarenewable@gmail.com"
                      className="opacity-80 hover:opacity-100 hover:text-primary-green transition-all duration-300"
                    >
                      corenovarenewable@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Visit Us</div>
                    <a
                      href="https://maps.google.com/?q=Plot+no+-109,+Old+Ag+Colony,+Unit+4,+Madhusudan+Nagar,+Bhubaneswar,+Odisha+751001"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 hover:text-primary-green transition-all duration-300"
                    >
                      Plot no -109, Old Ag Colony, Unit 4, Madhusudan Nagar, Bhubaneswar, Odisha 751001
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6">Get Free Consultation</h3>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder="Your Name"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange} className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-white/50"
                  required
                />
                <input
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange} placeholder="Your Email"
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-white/50"
                  required
                />
                <input
                  type="tel"
                                          id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                  placeholder="Your Phone Number"
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-white/50"
                />
                <textarea
                  placeholder="Tell us about your project"
                  rows="4"
                                        id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:border-white/50 resize-none"
                  required
                ></textarea>
                <button
                  type="submit"
                  className="w-full btn btn-primary bg-gradient-to-r from-primary-green to-secondary-blue hover:shadow-xl flex items-center justify-center gap-2"
                >
                  Send Message
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-br from-dark to-gray-800 text-white">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto space-y-8 animate-on-scroll">
            <h2 className="text-4xl md:text-6xl font-bold leading-tight">
              Ready to Go Green?
            </h2>
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
              Join thousands of satisfied customers who have already made the switch to renewable energy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="btn btn-primary text-lg px-8 py-4 group inline-flex items-center justify-center gap-2"
              >
                Start Your Journey
                <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="btn btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-dark inline-flex items-center justify-center"
              >
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 